import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import {
  TASK_KIND_LABELS,
  TASK_STATUS_LABELS,
  type TaskKind,
  type TaskStatus,
  type TaskEntityType,
} from "@ckgo2/shared/types";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Circle,
  CalendarDays,
  RefreshCw,
} from "lucide-react";
import i18n, { uiLocale } from "@/i18n";
import { enumLabel } from "@/i18n/enumLabels";

interface TaskRow {
  id: string;
  entityType: TaskEntityType;
  entityId: string;
  kind: TaskKind;
  title: string;
  dueDate?: string | null;
  status: TaskStatus;
  assignedToName?: string | null;
  orderNumber?: string | null;
}

interface FacetedTasks {
  data: TaskRow[];
  total: number;
  success: true;
}

const KIND_DOT: Record<TaskKind, string> = {
  deadline: "bg-amber-500",
  readiness: "bg-blue-500",
  callback: "bg-purple-500",
  manual: "bg-gray-400",
  supplier_followup: "bg-indigo-500",
};

const WEEKDAYS = [
  i18n.t("ops.calendar.weekdays.mon"),
  i18n.t("ops.calendar.weekdays.tue"),
  i18n.t("ops.calendar.weekdays.wed"),
  i18n.t("ops.calendar.weekdays.thu"),
  i18n.t("ops.calendar.weekdays.fri"),
  i18n.t("ops.calendar.weekdays.sat"),
  i18n.t("ops.calendar.weekdays.sun"),
];

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** First Monday-of-grid through the 6-week span covering a given month. */
function monthGrid(year: number, month: number): Date[] {
  const first = new Date(Date.UTC(year, month, 1));
  // JS getUTCDay: 0=Sun..6=Sat; shift so Monday=0.
  const lead = (first.getUTCDay() + 6) % 7;
  const start = new Date(first);
  start.setUTCDate(1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    return d;
  });
}

function entityLink(t: TaskRow): string | null {
  if (t.entityType === "order") return `/orders/${t.entityId}`;
  if (t.entityType === "lead") return `/leads/${t.entityId}`;
  return null;
}

export default function CalendarPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth());
  const [mineOnly, setMineOnly] = useState(false);
  const [selected, setSelected] = useState<string>(ymd(now));

  // Scope the fetch to the visible 6-week grid so navigating months loads that month's tasks (the
  // old fixed `limit=200` showed only the first 200 ever, missing anything outside that page).
  const grid = useMemo(() => monthGrid(year, month), [year, month]);
  const gridFrom = ymd(grid[0]);
  const gridTo = ymd(grid[grid.length - 1]);
  const qs = mineOnly ? "&mine=1" : "";
  const { data, isFetching } = useQuery({
    queryKey: ["calendar-tasks", mineOnly, gridFrom, gridTo],
    queryFn: () =>
      api.get<FacetedTasks>(`/api/admin/tasks?limit=200&dueFrom=${gridFrom}&dueTo=${gridTo}${qs}`),
  });

  const scan = useMutation({
    mutationFn: () => api.post("/api/admin/tasks/scan", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar-tasks"] }),
  });

  const toggle = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.patch(`/api/admin/tasks/${id}/status`, { status: done ? "done" : "open" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar-tasks"] }),
  });

  const tasks = useMemo(() => data?.data ?? [], [data]);
  const byDay = useMemo(() => {
    const m = new Map<string, TaskRow[]>();
    for (const t of tasks) {
      if (!t.dueDate) continue;
      const key = t.dueDate.slice(0, 10);
      const arr = m.get(key) ?? [];
      arr.push(t);
      m.set(key, arr);
    }
    return m;
  }, [tasks]);

  const noDue = tasks.filter((t) => !t.dueDate);
  const todayKey = ymd(now);
  const selectedTasks = byDay.get(selected) ?? [];

  const prevMonth = () => {
    const d = new Date(Date.UTC(year, month - 1, 1));
    setYear(d.getUTCFullYear());
    setMonth(d.getUTCMonth());
  };
  const nextMonth = () => {
    const d = new Date(Date.UTC(year, month + 1, 1));
    setYear(d.getUTCFullYear());
    setMonth(d.getUTCMonth());
  };

  const monthLabel = new Date(Date.UTC(year, month, 1)).toLocaleDateString(uiLocale(), {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <CalendarDays className="h-6 w-6" /> {t("ops.calendar.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("ops.calendar.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
              className="rounded"
            />
            {t("ops.calendar.onlyMine")}
          </label>
          <button
            onClick={() => scan.mutate()}
            disabled={scan.isPending}
            className="btn-ghost btn-sm"
            title={t("ops.calendar.scanTitle")}
          >
            <RefreshCw className={`h-4 w-4 ${scan.isPending ? "animate-spin" : ""}`} />{" "}
            {t("ops.calendar.scan")}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Month grid */}
        <div className="card lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <button onClick={prevMonth} className="btn-ghost btn-sm">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {monthLabel}
              {isFetching && <span className="ml-2 text-xs text-gray-400">…</span>}
            </h2>
            <button onClick={nextMonth} className="btn-ghost btn-sm">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((d) => (
              <div key={d} className="pb-1 text-center text-xs font-medium uppercase text-gray-400">
                {d}
              </div>
            ))}
            {grid.map((d) => {
              const key = ymd(d);
              const inMonth = d.getUTCMonth() === month;
              const dayTasks = byDay.get(key) ?? [];
              const isToday = key === todayKey;
              const isSelected = key === selected;
              return (
                <button
                  key={key}
                  onClick={() => setSelected(key)}
                  className={`min-h-[64px] rounded-lg border p-1 text-left transition-colors ${
                    isSelected
                      ? "border-primary-500 ring-primary-500/30 ring-1"
                      : "border-gray-100 dark:border-gray-700"
                  } ${inMonth ? "bg-white dark:bg-gray-800" : "bg-gray-50 dark:bg-gray-900/40"} hover:border-primary-300`}
                >
                  <div
                    className={`mb-1 text-xs ${
                      isToday
                        ? "bg-primary-600 inline-flex h-5 w-5 items-center justify-center rounded-full font-bold text-white"
                        : inMonth
                          ? "text-gray-700 dark:text-gray-300"
                          : "text-gray-400"
                    }`}
                  >
                    {d.getUTCDate()}
                  </div>
                  <div className="flex flex-wrap gap-0.5">
                    {dayTasks.slice(0, 4).map((t) => (
                      <span
                        key={t.id}
                        className={`h-1.5 w-1.5 rounded-full ${KIND_DOT[t.kind]} ${
                          t.status === "done" ? "opacity-30" : ""
                        }`}
                        title={t.title}
                      />
                    ))}
                    {dayTasks.length > 4 && (
                      <span className="text-[9px] text-gray-400">+{dayTasks.length - 4}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-3 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700">
            {(Object.keys(KIND_DOT) as TaskKind[]).map((k) => (
              <span key={k} className="flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${KIND_DOT[k]}`} />{" "}
                {enumLabel(TASK_KIND_LABELS, k)}
              </span>
            ))}
          </div>
        </div>

        {/* Selected day + undated */}
        <div className="space-y-4">
          <div className="card">
            <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">
              {new Date(selected + "T00:00:00Z").toLocaleDateString(uiLocale(), {
                weekday: "long",
                month: "short",
                day: "numeric",
                timeZone: "UTC",
              })}
            </h3>
            {selectedTasks.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">
                {t("ops.calendar.nothingScheduled")}
              </p>
            ) : (
              <ul className="space-y-2">
                {selectedTasks.map((t) => (
                  <TaskItem key={t.id} t={t} todayKey={todayKey} toggle={toggle} />
                ))}
              </ul>
            )}
          </div>

          {noDue.length > 0 && (
            <div className="card">
              <h3 className="mb-3 text-sm font-semibold text-gray-500">
                {t("ops.calendar.noDueDate")}
              </h3>
              <ul className="space-y-2">
                {noDue.slice(0, 20).map((t) => (
                  <TaskItem key={t.id} t={t} todayKey={todayKey} toggle={toggle} />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskItem({
  t,
  todayKey,
  toggle,
}: {
  t: TaskRow;
  todayKey: string;
  toggle: ReturnType<typeof useMutation<unknown, Error, { id: string; done: boolean }>>;
}) {
  const overdue = t.dueDate && t.dueDate.slice(0, 10) < todayKey && t.status === "open";
  const link = entityLink(t);
  const done = t.status === "done";
  return (
    <li className="flex items-start gap-2">
      <button
        onClick={() => toggle.mutate({ id: t.id, done: !done })}
        className="mt-0.5"
        title={done ? i18n.t("ops.calendar.reopen") : i18n.t("ops.calendar.markDone")}
      >
        {done ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        ) : (
          <Circle className={`h-4 w-4 ${overdue ? "text-red-500" : "text-gray-400"}`} />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm ${done ? "text-gray-400 line-through" : "text-gray-800 dark:text-gray-200"}`}
        >
          {t.title}
        </p>
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
          <span className={`rounded px-1 ${overdue ? "text-red-500" : ""}`}>
            {enumLabel(TASK_STATUS_LABELS, t.status)}
          </span>
          {link && t.orderNumber && (
            <Link to={link} className="text-primary-600 dark:text-primary-400 font-mono">
              {t.orderNumber}
            </Link>
          )}
          {t.assignedToName && <span>{t.assignedToName}</span>}
        </p>
      </div>
    </li>
  );
}
